/* ===========================================================================
   Maison Brassel — Bundle Offer engine (progressive unlock + free gift)
   ---------------------------------------------------------------------------
   Reads active offers from <script data-bundle-offers> (emitted by the
   `bundle-offer-rules` snippet from the `bundle_offer` metaobject) and:
     1. Renders the 3-state widget on every [data-bundle-offer-bar] shell.
     2. Auto-adds the reward (gift) line when the threshold is reached, and
        auto-removes it (only its own tagged line) when it drops below.
   Pricing (gift = 0 EUR) is handled server-side by the matching Shopify
   automatic "Buy X Get Y" discount — this engine only manages presence/UI.

   Adapted from Belvans `collection-service-cart-validator.js`
   (same fetch-patch + single-flight validation loop pattern).
   All dynamic text is injected via textContent (no innerHTML / XSS-safe).
   =========================================================================== */
(function (globalScope) {
  'use strict';

  var OFFERS_SELECTOR = '[data-bundle-offers]';
  var BAR_SELECTOR = '[data-bundle-offer-bar]';
  var GIFT_FLAG = '_bundle_gift';
  var GIFT_OFFER_ID = '_bundle_offer_id';
  var GIFT_FLAG_VALUE = 'true';
  var CART_REFRESH_EVENT = 'theme:cart:refresh';
  var JSON_TYPE = 'application/json';
  var REWARD_TRIGGER_SELECTOR = '[data-bundle-reward-trigger]';
  var REWARD_MODAL_SELECTOR = '[data-bundle-reward-modal]';
  var REWARD_MODAL_OPEN_CLASS = 'is-open';
  var REWARD_MODAL_BODY_CLASS = 'bundle-reward-modal-is-open';
  var REWARD_LINK_LABEL = 'Voir le produit offert';

  var validationPromise = null;
  var validationRequested = false;
  var pendingCart = null;
  var isApplyingCartActions = false;

  /* ----------------------------------------------------------------- utils */
  function normInt(value) {
    var n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function getRoot() {
    return (
      (globalScope.Shopify && globalScope.Shopify.routes && globalScope.Shopify.routes.root) ||
      (globalScope.theme && globalScope.theme.routes && globalScope.theme.routes.root) ||
      '/'
    );
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function svgEl(tag, attrs) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.keys(attrs || {}).forEach(function (key) {
      node.setAttribute(key, attrs[key]);
    });
    return node;
  }

  function createStateIcon(pathData, className) {
    var svg = svgEl('svg', {
      'aria-hidden': 'true',
      focusable: 'false',
      role: 'presentation',
      class: 'bundle-bar__state-icon ' + className,
      viewBox: '0 0 24 24',
    });
    var path = svgEl('path', {
      d: pathData,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.25',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
    svg.appendChild(path);
    return svg;
  }

  function createIconForState(state) {
    if (state === 1) {
      return createStateIcon(
        'M16 12h1.4a.6.6 0 0 1 .6.6v6.8a.6.6 0 0 1-.6.6H6.6a.6.6 0 0 1-.6-.6v-6.8a.6.6 0 0 1 .6-.6H8m8 0V8c0-1.333-.8-4-4-4S8 6.667 8 8v4m8 0H8',
        'bundle-bar__state-icon--lock'
      );
    }
    if (state === 2) {
      return createStateIcon(
        'm7 12.5 3 3 7-7M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z',
        'bundle-bar__state-icon--check'
      );
    }
    return createStateIcon(
      'M20 12v9.4a.6.6 0 0 1-.6.6H4.6a.6.6 0 0 1-.6-.6V12m17.4-5H2.6a.6.6 0 0 0-.6.6v3.8a.6.6 0 0 0 .6.6h18.8a.6.6 0 0 0 .6-.6V7.6a.6.6 0 0 0-.6-.6zM12 22V7m0 0H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z',
      'bundle-bar__state-icon--gift'
    );
  }

  function createArrowIcon() {
    var svg = svgEl('svg', {
      'aria-hidden': 'true',
      focusable: 'false',
      viewBox: '0 0 16 16',
      class: 'bundle-bar__reward-link-icon',
    });
    var path = svgEl('path', {
      d: 'M5.5 3.5 10 8l-4.5 4.5M10 8H2.5',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.35',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
    svg.appendChild(path);
    return svg;
  }

  function text(value) {
    return document.createTextNode(value == null ? '' : String(value));
  }

  function readJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      console.error('[BundleOffer] JSON parse error', error);
      return null;
    }
  }

  function readOffers() {
    var data = readJSON(document.querySelector(OFFERS_SELECTOR));
    if (!Array.isArray(data)) return [];
    return data.filter(function (o) {
      return o && o.reward && normInt(o.reward.variantId) > 0 && normInt(o.minQuantity) > 0;
    });
  }

  function readBarConfig(bar) {
    var cfg = readJSON(bar.querySelector('[data-bundle-bar-config]')) || {};
    cfg.icons = cfg.icons || {};
    return cfg;
  }

  function fillTokens(str, ctx) {
    return String(str || '')
      .replace(/\{remaining\}/g, ctx.remaining)
      .replace(/\{gift_value\}/g, ctx.giftValue)
      .replace(/\{savings\}/g, ctx.savings)
      .replace(/\{suggestion_name\}/g, ctx.suggestionName);
  }

  function lineProps(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function isGiftLineFor(item, offer) {
    var p = lineProps(item);
    return p[GIFT_FLAG] === GIFT_FLAG_VALUE && String(p[GIFT_OFFER_ID]) === String(offer.id);
  }

  /* ----------------------------------------------------------- cart reads */
  function triggerIdSet(offer) {
    var set = Object.create(null);
    (offer.triggerProducts || []).forEach(function (p) {
      var id = normInt(p.productId);
      if (id) set[id] = true;
    });
    return set;
  }

  // Trigger quantity in cart + images of the matching units (for slot fills).
  function countTriggers(cart, offer) {
    var ids = triggerIdSet(offer);
    var rewardProductId = normInt(offer.reward.productId);
    var count = 0;
    var images = [];

    (cart.items || []).forEach(function (item) {
      var pid = normInt(item.product_id);
      if (!pid || pid === rewardProductId) return;
      if (isGiftLineFor(item, offer)) return;
      if (!ids[pid]) return;

      var qty = normInt(item.quantity);
      count += qty;
      for (var i = 0; i < qty; i++) {
        images.push((item.featured_image && item.featured_image.url) || item.image || '');
      }
    });

    return { count: count, images: images };
  }

  function giftLinesFor(cart, offer) {
    return (cart.items || []).filter(function (item) {
      return isGiftLineFor(item, offer);
    });
  }

  function rewardQtyInCart(cart, offer) {
    var rewardProductId = normInt(offer.reward.productId);
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (normInt(item.product_id) === rewardProductId) total += normInt(item.quantity);
    });
    return total;
  }

  /* ------------------------------------------------------- gift add/remove */
  function computeActions(cart, offers) {
    var additions = [];
    var updates = {};

    offers.forEach(function (offer) {
      var minQty = normInt(offer.minQuantity);
      var rewardQty = normInt(offer.reward.quantity) || 1;
      var count = countTriggers(cart, offer).count;
      var desired = count >= minQty ? rewardQty : 0;
      var giftLines = giftLinesFor(cart, offer);

      if (desired > 0) {
        if (giftLines.length === 0) {
          // Only auto-add when the reward product isn't already in the cart
          // (don't duplicate a manually added etui).
          if (rewardQtyInCart(cart, offer) === 0) {
            var props = {};
            props[GIFT_FLAG] = GIFT_FLAG_VALUE;
            props[GIFT_OFFER_ID] = String(offer.id);
            additions.push({ id: normInt(offer.reward.variantId), quantity: desired, properties: props });
          }
        } else {
          giftLines.forEach(function (line, index) {
            var next = index === 0 ? desired : 0;
            if (line.key && normInt(line.quantity) !== next) updates[line.key] = next;
          });
        }
      } else {
        // Locked: remove only lines this engine tagged.
        giftLines.forEach(function (line) {
          if (line.key) updates[line.key] = 0;
        });
      }
    });

    return { additions: additions, updates: updates };
  }

  function hasActions(actions) {
    return Boolean(actions.additions.length) || Object.keys(actions.updates).length > 0;
  }

  function postJSON(path, body) {
    return fetch(getRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': JSON_TYPE, Accept: JSON_TYPE, 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  function applyActions(actions) {
    isApplyingCartActions = true;
    var chain = Promise.resolve();

    if (Object.keys(actions.updates).length > 0) {
      chain = chain.then(function () {
        return postJSON('cart/update.js', { updates: actions.updates });
      });
    }
    if (actions.additions.length > 0) {
      chain = chain.then(function () {
        return postJSON('cart/add.js', { items: actions.additions });
      });
    }

    return chain
      .catch(function (error) {
        console.error('[BundleOffer] cart action error', error);
      })
      .then(function () {
        isApplyingCartActions = false;
        document.dispatchEvent(new CustomEvent(CART_REFRESH_EVENT, { bubbles: true }));
      });
  }

  /* --------------------------------------------------------- offer/bar map */
  function matchOffer(bar, offers) {
    var cfg = readBarConfig(bar);
    if (cfg.offerId) {
      return (
        offers.filter(function (o) {
          return o.id === cfg.offerId;
        })[0] || null
      );
    }

    var loc = bar.getAttribute('data-location') || 'pdp';
    var productId = normInt(bar.getAttribute('data-product-id'));

    var candidates = offers.filter(function (o) {
      var locs = o.displayLocations || [];
      return !locs.length || locs.indexOf(loc) !== -1;
    });

    // On a product page, only surface an offer the current product is part of.
    if (loc === 'pdp' && productId) {
      candidates = candidates.filter(function (o) {
        return (o.triggerProducts || []).some(function (p) {
          return normInt(p.productId) === productId;
        });
      });
    }

    candidates.sort(function (a, b) {
      return (b.priority || 0) - (a.priority || 0);
    });
    return candidates[0] || null;
  }

  /* ------------------------------------------------------------- rendering */
  function renderSlots(container, offer, triggers) {
    var minQty = normInt(offer.minQuantity);
    var images = triggers.images || [];
    container.textContent = '';

    for (var i = 0; i < minQty; i++) {
      if (i > 0) {
        var plus = el('span', 'bundle-bar__plus');
        plus.appendChild(text('+'));
        container.appendChild(plus);
      }
      var slot = el('span', 'bundle-bar__slot');
      if (images[i]) {
        slot.className += ' bundle-bar__slot--filled';
        var img = el('img');
        img.src = images[i];
        img.alt = '';
        img.loading = 'lazy';
        slot.appendChild(img);
      }
      container.appendChild(slot);
    }

    var eq = el('span', 'bundle-bar__plus');
    eq.appendChild(text('='));
    container.appendChild(eq);

    var hasRewardMedia = offer.reward && (offer.reward.image || (offer.reward.media || []).length);
    var giftSlotCanOpen = hasRewardMedia && offer.reward.image;
    var giftSlot = el(giftSlotCanOpen ? 'button' : 'span', 'bundle-bar__slot bundle-bar__slot--gift');
    if (giftSlotCanOpen) {
      giftSlot.type = 'button';
      giftSlot.className += ' bundle-bar__slot--button';
      giftSlot.setAttribute('data-bundle-reward-trigger', offer.id || '');
      giftSlot.setAttribute('aria-label', REWARD_LINK_LABEL);
    }
    if (offer.reward && offer.reward.image) {
      var giftImg = el('img');
      giftImg.src = offer.reward.image;
      giftImg.alt = giftSlotCanOpen ? '' : offer.reward.title || '';
      giftImg.loading = 'lazy';
      giftSlot.appendChild(giftImg);
    }
    container.appendChild(giftSlot);

    if (hasRewardMedia) {
      var rewardLink = el('button', 'bundle-bar__reward-link');
      rewardLink.type = 'button';
      rewardLink.setAttribute('data-bundle-reward-trigger', offer.id || '');
      var rewardLinkText = el('span', 'bundle-bar__reward-link-text');
      rewardLinkText.appendChild(text(REWARD_LINK_LABEL));
      rewardLink.appendChild(rewardLinkText);
      rewardLink.appendChild(createArrowIcon());
      container.appendChild(rewardLink);
    }
  }

  function renderBar(bar, offer, cart) {
    var cfg = readBarConfig(bar);
    var minQty = normInt(offer.minQuantity);
    var triggers = countTriggers(cart, offer);
    var count = triggers.count;
    var state = count <= 0 ? 0 : count < minQty ? 1 : 2;

    var ctx = {
      remaining: Math.max(0, minQty - count),
      giftValue: (offer.reward && offer.reward.value) || '',
      savings: (offer.reward && offer.reward.value) || '',
      suggestionName: offer.suggestion ? offer.suggestion.title : '',
    };

    var messages = offer.messages || {};
    var headlineSrc = state === 0 ? messages.state0 : state === 1 ? messages.state1 : messages.state2;

    bar.setAttribute('data-state', String(state));

    var iconEl = bar.querySelector('[data-bundle-icon]');
    if (iconEl) {
      iconEl.textContent = '';
      iconEl.classList.remove('bundle-bar__icon--image');
      iconEl.appendChild(createIconForState(state));
    }

    var headlineEl = bar.querySelector('[data-bundle-headline]');
    if (headlineEl) headlineEl.textContent = fillTokens(headlineSrc, ctx);

    var fillEl = bar.querySelector('[data-bundle-fill]');
    if (fillEl) fillEl.style.width = Math.min(100, Math.round((count / minQty) * 100)) + '%';

    var countLabel = bar.querySelector('[data-bundle-count-label]');
    if (countLabel) {
      countLabel.textContent = '';
      var strong = el('strong');
      strong.appendChild(text(Math.min(count, minQty) + ' / ' + minQty));
      countLabel.appendChild(strong);
      countLabel.appendChild(text(' ' + (cfg.countSuffix || '')));
    }

    var valueLabel = bar.querySelector('[data-bundle-value-label]');
    if (valueLabel) {
      valueLabel.textContent = '';
      if (state === 2) {
        var sv = el('strong');
        sv.appendChild(text(fillTokens(messages.savingsLabel, ctx)));
        valueLabel.appendChild(sv);
      } else {
        valueLabel.appendChild(text(ctx.giftValue + ' ' + (cfg.valueLockedSuffix || '')));
      }
    }

    var slots = bar.querySelector('[data-bundle-slots]');
    if (slots) renderSlots(slots, offer, triggers);

    bar.hidden = false;
  }

  function renderAll(cart, offers) {
    document.querySelectorAll(BAR_SELECTOR).forEach(function (bar) {
      var offer = matchOffer(bar, offers);
      if (!offer) {
        bar.hidden = true;
        return;
      }
      renderBar(bar, offer, cart);
    });
  }

  /* ---------------------------------------------------------- reward modal */
  function rewardMedia(offer) {
    var reward = (offer && offer.reward) || {};
    var media = Array.isArray(reward.media) ? reward.media.slice() : [];
    if (!media.length && reward.image) {
      media.push({ type: 'image', image: reward.image, thumb: reward.image, alt: reward.title || '' });
    }
    return media.filter(function (item) {
      if (!item) return false;
      if (item.type === 'video') return Boolean(item.src && (item.poster || item.thumb));
      return Boolean(item.image || item.poster || item.thumb);
    });
  }

  function mediaPoster(item) {
    return item ? item.image || item.poster || item.thumb || '' : '';
  }

  function pauseRewardVideos(modal) {
    if (!modal) return;
    modal.querySelectorAll('video').forEach(function (video) {
      video.pause();
    });
  }

  function findOfferById(id) {
    return (
      readOffers().filter(function (offer) {
        return String(offer.id) === String(id);
      })[0] || null
    );
  }

  function ensureRewardModal() {
    var modal = document.querySelector(REWARD_MODAL_SELECTOR);
    if (modal) return modal;

    modal = el('div', 'bundle-reward-modal');
    modal.setAttribute('data-bundle-reward-modal', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;

    modal.innerHTML =
      '<div class="bundle-reward-modal__backdrop" data-bundle-reward-close></div>' +
      '<section class="bundle-reward-modal__sheet" role="dialog" aria-modal="true" aria-labelledby="bundle-reward-modal-title">' +
      '<header class="bundle-reward-modal__head">' +
      '<p class="bundle-reward-modal__title" id="bundle-reward-modal-title"></p>' +
      '<button class="bundle-reward-modal__close" type="button" data-bundle-reward-close aria-label="Fermer">' +
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '</button>' +
      '</header>' +
      '<div class="bundle-reward-modal__body">' +
      '<div class="bundle-reward-modal__main" data-bundle-reward-main></div>' +
      '<div class="bundle-reward-modal__thumbs" data-bundle-reward-thumbs></div>' +
      '</div>' +
      '</section>';

    document.body.appendChild(modal);
    return modal;
  }

  function setRewardModalImage(modal, media, index) {
    var main = modal.querySelector('[data-bundle-reward-main]');
    var item = media[index] || media[0];
    if (!main || !item) return;
    pauseRewardVideos(modal);
    main.textContent = '';

    if (item.type === 'video' && item.src) {
      var video = el('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-label', item.alt || '');
      if (item.poster) video.poster = item.poster;

      var source = el('source');
      source.src = item.src;
      source.type = item.mimeType || 'video/mp4';
      video.appendChild(source);
      main.appendChild(video);
    } else {
      var image = el('img');
      image.src = mediaPoster(item);
      image.alt = item.alt || '';
      image.loading = 'eager';
      main.appendChild(image);
    }

    modal.querySelectorAll('[data-bundle-reward-thumb]').forEach(function (thumb, thumbIndex) {
      thumb.classList.toggle('is-active', thumbIndex === index);
      thumb.setAttribute('aria-pressed', thumbIndex === index ? 'true' : 'false');
    });
  }

  function openRewardModal(offer) {
    var media = rewardMedia(offer);
    if (!offer || !offer.reward || !media.length) return;

    var modal = ensureRewardModal();
    var title = modal.querySelector('.bundle-reward-modal__title');
    var thumbs = modal.querySelector('[data-bundle-reward-thumbs]');
    if (title) title.textContent = offer.reward.title || '';
    if (thumbs) {
      thumbs.textContent = '';
      media.forEach(function (item, index) {
        var thumb = el('button', 'bundle-reward-modal__thumb');
        thumb.type = 'button';
        if (item.type === 'video') thumb.className += ' bundle-reward-modal__thumb--video';
        thumb.setAttribute('data-bundle-reward-thumb', String(index));
        thumb.setAttribute('aria-label', (item.alt || offer.reward.title || 'Media') + ' ' + (index + 1));
        var img = el('img');
        img.src = item.thumb || mediaPoster(item);
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        if (item.type === 'video') {
          var play = el('span', 'bundle-reward-modal__play');
          play.setAttribute('aria-hidden', 'true');
          thumb.appendChild(play);
        }
        thumb.addEventListener('click', function () {
          setRewardModalImage(modal, media, index);
        });
        thumbs.appendChild(thumb);
      });
    }

    setRewardModalImage(modal, media, 0);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add(REWARD_MODAL_BODY_CLASS);
    requestAnimationFrame(function () {
      modal.classList.add(REWARD_MODAL_OPEN_CLASS);
      var close = modal.querySelector('.bundle-reward-modal__close');
      if (close) {
        try {
          close.focus({ preventScroll: true });
        } catch (error) {
          close.focus();
        }
      }
    });
  }

  function closeRewardModal() {
    var modal = document.querySelector(REWARD_MODAL_SELECTOR);
    if (!modal || modal.hidden) return;
    modal.classList.remove(REWARD_MODAL_OPEN_CLASS);
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(REWARD_MODAL_BODY_CLASS);
    pauseRewardVideos(modal);
    setTimeout(function () {
      if (!modal.classList.contains(REWARD_MODAL_OPEN_CLASS)) modal.hidden = true;
    }, 340);
  }

  function onRewardModalClick(event) {
    var trigger = event.target.closest ? event.target.closest(REWARD_TRIGGER_SELECTOR) : null;
    if (trigger) {
      event.preventDefault();
      openRewardModal(findOfferById(trigger.getAttribute('data-bundle-reward-trigger')));
      return;
    }
    if (event.target.closest && event.target.closest('[data-bundle-reward-close]')) {
      event.preventDefault();
      closeRewardModal();
    }
  }

  function onRewardModalKeydown(event) {
    if (event.key === 'Escape') closeRewardModal();
  }

  /* ----------------------------------------------------- validation cycle */
  function fetchCart() {
    return fetch(getRoot() + 'cart.js', { headers: { Accept: JSON_TYPE } }).then(function (r) {
      return r.json();
    });
  }

  function validate(cartArg) {
    var offers = readOffers();
    if (!offers.length) return Promise.resolve();

    return Promise.resolve(cartArg || fetchCart())
      .then(function (cart) {
        var actions = computeActions(cart, offers);
        if (hasActions(actions)) {
          return applyActions(actions)
            .then(fetchCart)
            .then(function (fresh) {
              renderAll(fresh, offers);
            });
        }
        renderAll(cart, offers);
      })
      .catch(function (error) {
        console.error('[BundleOffer] validation error', error);
      });
  }

  function requestValidation(cartPromise) {
    validationRequested = true;
    if (cartPromise) pendingCart = cartPromise;
    if (validationPromise) return validationPromise;

    validationPromise = (function () {
      function step() {
        if (!validationRequested) return Promise.resolve();
        validationRequested = false;
        var next = pendingCart;
        pendingCart = null;
        return Promise.resolve(next ? next : null).then(validate).then(step);
      }
      return step();
    })().then(function () {
      validationPromise = null;
    });

    return validationPromise;
  }

  /* ------------------------------------------------------ fetch patching */
  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function shouldRevalidate(url) {
    return url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1;
  }

  function patchFetch() {
    if (globalScope.__brasselBundleOfferFetchPatched) return;
    var originalFetch = globalScope.fetch;
    globalScope.__brasselBundleOfferFetchPatched = true;

    globalScope.fetch = function patchedFetch() {
      var args = arguments;
      var requestUrl = getRequestUrl(args[0]);
      return originalFetch.apply(this, args).then(function (response) {
        var url = (response && response.url) || requestUrl || '';
        if (!isApplyingCartActions && shouldRevalidate(url)) {
          requestValidation();
        }
        return response;
      });
    };
  }

  /* -------------------------------------------------------------- bootstrap */
  function init() {
    if (!document.querySelector(OFFERS_SELECTOR)) return;
    document.addEventListener('click', onRewardModalClick);
    document.addEventListener('keydown', onRewardModalKeydown);
    document.addEventListener(CART_REFRESH_EVENT, function (event) {
      requestValidation(Promise.resolve((event && event.detail && event.detail.cart) || null));
    });
    patchFetch();
    requestValidation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  globalScope.BrasselBundleOffer = {
    readOffers: readOffers,
    computeActions: computeActions,
    countTriggers: countTriggers,
    matchOffer: matchOffer,
    requestValidation: requestValidation,
  };
})(typeof window !== 'undefined' ? window : globalThis);
